import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { encodeCanvas } from "../encode";
import type { Strings } from "../i18n";

/**
 * Editor de marcas sobre la captura, antes de enviarla: recuadros, flechas, trazo libre y difuminar (para
 * tapar datos que no deben salir). Trabaja sobre la imagen ya capturada, a su tamaño real; las marcas se
 * pintan encima y la imagen final sustituye a la captura.
 */

type Tool = "box" | "arrow" | "pen" | "blur";
const TOOLS: Tool[] = ["box", "arrow", "pen", "blur"];
type P = { x: number; y: number };
interface Shape {
  tool: Tool;
  points: P[];
}

function paintShape(ctx: CanvasRenderingContext2D, s: Shape, img: HTMLImageElement, color: string): void {
  const first = s.points[0];
  const last = s.points[s.points.length - 1];
  if (!first || !last) return;
  const lw = Math.max(3, img.width / 320);
  ctx.save();
  ctx.lineWidth = lw;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  const x = Math.min(first.x, last.x);
  const y = Math.min(first.y, last.y);
  const w = Math.abs(last.x - first.x);
  const h = Math.abs(last.y - first.y);
  if (s.tool === "box") ctx.strokeRect(x, y, w, h);
  else if (s.tool === "blur" && w >= 2 && h >= 2) {
    // Pixelado a partir de la imagen original: se ve que ahí había algo, pero no qué.
    const cell = Math.max(6, Math.round(img.width / 120));
    const small = document.createElement("canvas");
    small.width = Math.max(1, Math.round(w / cell));
    small.height = Math.max(1, Math.round(h / cell));
    small.getContext("2d")?.drawImage(img, x, y, w, h, 0, 0, small.width, small.height);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(small, 0, 0, small.width, small.height, x, y, w, h);
  } else if (s.tool === "arrow") {
    const angle = Math.atan2(last.y - first.y, last.x - first.x);
    const head = lw * 4.5;
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    ctx.lineTo(last.x - Math.cos(angle) * head * 0.6, last.y - Math.sin(angle) * head * 0.6);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(last.x - head * Math.cos(angle - Math.PI / 7), last.y - head * Math.sin(angle - Math.PI / 7));
    ctx.lineTo(last.x - head * Math.cos(angle + Math.PI / 7), last.y - head * Math.sin(angle + Math.PI / 7));
    ctx.closePath();
    ctx.fill();
  } else if (s.tool === "pen") {
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (const p of s.points.slice(1)) ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }
  ctx.restore();
}

export function Draw({ image, color, t, onDone, onCancel }: { image: Blob; color: string; t: Strings; onDone: (marked: Blob) => void; onCancel: () => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const first = useRef<HTMLButtonElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<Tool>("box");
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [busy, setBusy] = useState(false);
  const draft = useRef<Shape | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(image);
    const i = new Image();
    i.onload = () => setImg(i);
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const paint = useCallback(() => {
    const c = canvas.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx || !img) return;
    if (c.width !== img.width) c.width = img.width;
    if (c.height !== img.height) c.height = img.height;
    ctx.drawImage(img, 0, 0);
    for (const s of draft.current ? [...shapes, draft.current] : shapes) paintShape(ctx, s, img, color);
  }, [img, shapes, color]);
  useLayoutEffect(paint, [paint]);
  useLayoutEffect(() => first.current?.focus(), []);

  const at = (e: PointerEvent): P => {
    const c = canvas.current!;
    const r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * c.width) / r.width, y: ((e.clientY - r.top) * c.height) / r.height };
  };

  async function finish() {
    const c = canvas.current;
    if (!c || busy) return;
    setBusy(true);
    draft.current = null;
    paint();
    const blob = await encodeCanvas(c);
    setBusy(false);
    if (blob) onDone(blob);
  }

  return (
    <div
      class="draw"
      role="dialog"
      aria-label={t.drawTitle}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onCancel();
        } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
          e.preventDefault();
          setShapes((s) => s.slice(0, -1));
        }
      }}
    >
      <div class="draw-bar">
        <span class="draw-title">{t.drawTitle}</span>
        <div class="draw-tools" role="group" aria-label={t.drawTitle}>
          {TOOLS.map((k, i) => (
            <button key={k} ref={i === 0 ? first : undefined} type="button" aria-pressed={tool === k} onClick={() => setTool(k)}>
              {t.tools[k]}
            </button>
          ))}
          <button type="button" onClick={() => setShapes((s) => s.slice(0, -1))} disabled={!shapes.length}>
            {t.undo}
          </button>
        </div>
        <div class="draw-actions">
          <button type="button" class="btn" onClick={onCancel}>
            {t.cancel}
          </button>
          <button type="button" class="btn primary" onClick={() => void finish()} disabled={!img || busy}>
            {t.drawDone}
          </button>
        </div>
      </div>
      <div class="draw-stage">
        <canvas
          ref={canvas}
          aria-label={t.drawTitle}
          onPointerDown={(e) => {
            if (!img) return;
            e.preventDefault();
            (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
            draft.current = { tool, points: [at(e)] };
          }}
          onPointerMove={(e) => {
            const d = draft.current;
            if (!d) return;
            if (d.tool === "pen") d.points.push(at(e));
            else d.points = [d.points[0]!, at(e)];
            paint();
          }}
          onPointerUp={(e) => {
            const d = draft.current;
            draft.current = null;
            if (!d) return;
            const end = at(e);
            if (d.tool !== "pen") d.points = [d.points[0]!, end];
            const a = d.points[0]!;
            // Un clic sin arrastrar no es una marca.
            if (d.tool === "pen" ? d.points.length > 2 : Math.hypot(end.x - a.x, end.y - a.y) > 6) setShapes((s) => [...s, d]);
            else paint();
          }}
        />
      </div>
    </div>
  );
}
